import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import {useSearchParams} from "react-router-dom";
import type {ReadingContext, ReadingSelection, SourceInfo} from "../../../types";

/** The search param name that keeps the open island in the chat url. */
export const ISLAND_SEARCH_PARAM = "island";

export type WorkIslandMode = "content-wide" | "balanced" | "chat-wide";

export type WorkIslandRequest = {
    kind: "topic";
    source: SourceInfo;
    initialMode?: WorkIslandMode;
};

const CONTENT_FRACTIONS: Record<WorkIslandMode, number> = {
    "chat-wide": 0.3,
    balanced: 0.6,
    "content-wide": 0.7,
};

const modeFromFraction = (fraction: number): WorkIslandMode => {
    if (fraction < 0.4) return "chat-wide";
    if (fraction > 0.6) return "content-wide";
    return "balanced";
};

export type WorkIslandContextValue = {
    request: WorkIslandRequest | null;
    reportReadingContext: (context: ReadingContext | null) => void;
    selection: ReadingSelection | null;
    selectPassage: (selection: ReadingSelection) => void;
    clearSelection: () => void;
    mode: WorkIslandMode;
    contentFraction: number;
    openIsland: (request: WorkIslandRequest) => void;
    closeIsland: () => void;
    setMode: (mode: WorkIslandMode) => void;
    setContentFraction: (fraction: number) => void;
};

const defaultContext: WorkIslandContextValue = {
    request: null,
    reportReadingContext: () => {},
    selection: null,
    selectPassage: () => {},
    clearSelection: () => {},
    mode: "balanced",
    contentFraction: CONTENT_FRACTIONS.balanced,
    openIsland: () => {},
    closeIsland: () => {},
    setMode: () => {},
    setContentFraction: () => {},
};

const WorkIslandContext = createContext<WorkIslandContextValue>(defaultContext);

const ignoreReadingContext = (_context: ReadingContext | null) => {};

export function WorkIslandProvider({children, onReadingContextChange = ignoreReadingContext}: {
    children: ReactNode;
    onReadingContextChange?: (context: ReadingContext | null) => void;
}) {
    // The chat takes a copy at Send. Unmounting/switching threads must clear the live position.
    useEffect(() => () => onReadingContextChange(null), [onReadingContextChange]);
    const [request, setRequest] = useState<WorkIslandRequest | null>(null);
    const readingRef = useRef<ReadingContext | null>(null);
    const selectionRef = useRef<ReadingSelection | null>(null);
    const [selection, setSelection] = useState<ReadingSelection | null>(null);
    const clearSelection = useCallback(() => {
        selectionRef.current = null;
        setSelection(null);
        onReadingContextChange(readingRef.current);
    }, [onReadingContextChange]);
    const reportReadingContext = useCallback((reading: ReadingContext | null) => {
        if (!reading || reading.topicId !== readingRef.current?.topicId || reading.segmentId !== readingRef.current?.segmentId) {
            selectionRef.current = null;
            setSelection(null);
        }
        readingRef.current = reading;
        onReadingContextChange(reading ? {...reading, selection: selectionRef.current ?? undefined} : null);
    }, [onReadingContextChange]);
    const selectPassage = useCallback((quote: ReadingSelection) => {
        if (!readingRef.current) return;
        selectionRef.current = quote;
        setSelection(quote);
        onReadingContextChange({...readingRef.current, selection: quote});
    }, [onReadingContextChange]);

    const [contentFraction, setContentFractionState] = useState(
        CONTENT_FRACTIONS.balanced
    );
    const [searchParams, setSearchParams] = useSearchParams();

    const setContentFraction = useCallback((fraction: number) => {
        setContentFractionState(Math.min(0.7, Math.max(0.3, fraction)));
    }, []);

    const setMode = useCallback((mode: WorkIslandMode) => {
        setContentFractionState(CONTENT_FRACTIONS[mode]);
    }, []);

    // Write the open island into the url so a browser Back restores it.
    // Skip the write when the url already carries this exact island, for
    // example when a mount-time restore re-opens the island from the url.
    const requestUrl = request?.source.url;
    const openIsland = useCallback(
        (nextRequest: WorkIslandRequest) => {
            if (requestUrl !== nextRequest.source.url) reportReadingContext(null);
            setRequest(nextRequest);
            setContentFractionState(
                CONTENT_FRACTIONS[nextRequest.initialMode ?? "balanced"]
            );
            const encodedUrl = nextRequest.source.url;
            if (searchParams.get(ISLAND_SEARCH_PARAM) !== encodedUrl) {
                setSearchParams(
                    (prev) => {
                        const next = new URLSearchParams(prev);
                        next.set(ISLAND_SEARCH_PARAM, encodedUrl);
                        return next;
                    },
                    {replace: false}
                );
            }
        },
        [searchParams, setSearchParams, reportReadingContext, requestUrl]
    );

    const closeIsland = useCallback(() => {
        reportReadingContext(null);
        setRequest(null);
        // Always use the latest params, because a pending open write may not be applied yet.
        setSearchParams(
            (prev) => {
                if (!prev.has(ISLAND_SEARCH_PARAM)) return prev;
                const next = new URLSearchParams(prev);
                next.delete(ISLAND_SEARCH_PARAM);
                return next;
            },
            {replace: true}
        );
    }, [setSearchParams, reportReadingContext]);

    const value = useMemo(
        () => ({
            request,
            reportReadingContext,
            selection,
            selectPassage,
            clearSelection,
            mode: modeFromFraction(contentFraction),
            contentFraction,
            openIsland,
            closeIsland,
            setMode,
            setContentFraction,
        }),
        [
            reportReadingContext,
            selection,
            selectPassage,
            clearSelection,
            request,
            contentFraction,
            openIsland,
            closeIsland,
            setMode,
            setContentFraction,
        ]
    );

    return (
        <WorkIslandContext.Provider value={value}>
            {children}
        </WorkIslandContext.Provider>
    );
}

export function useWorkIsland(): WorkIslandContextValue {
    return useContext(WorkIslandContext);
}
